import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useBlocker } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import CustomerPicker from '../components/CustomerPicker';
import QuoteBreakInputs from '../components/QuoteBreakInputs';
import QuoteInternalEstimate from '../components/QuoteInternalEstimate';
import PillSelect from '../components/PillSelect';
import { Input, Textarea, FormGrid, Modal, ModalFooter, ModalButton, Select } from '../components/ui';
import { PAYMENT_TERMS_OPTIONS } from '../utils/poUtils';
import {
  priceAllBreaks,
  getQuoteWarnings,
  isQuoteValid,
  generateCustomerQuotePDF,
  generateInternalEstimatePDF,
} from '../utils/quoteUtils';
import { useLocalDraft, readLocalDraft, clearLocalDraft } from '../hooks/useLocalDraft';

const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'sent',      label: 'Sent' },
  { value: 'accepted',  label: 'Accepted' },
  { value: 'declined',  label: 'Declined' },
  { value: 'expired',   label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_PILL = {
  draft:     'bg-slate-100 text-slate-700 border-slate-300',
  sent:      'bg-sky-50 text-sky-700 border-sky-300',
  accepted:  'bg-emerald-50 text-emerald-700 border-emerald-300',
  declined:  'bg-rose-50 text-rose-700 border-rose-300',
  expired:   'bg-amber-50 text-amber-700 border-amber-300',
  cancelled: 'bg-slate-200 text-slate-700 border-slate-300',
};

const LABOR_INPUTS = [
  { field: 'shop_rate_per_hr',   label: 'Shop Rate ($/hr)',              step: '0.01', prefix: '$' },
  { field: 'setup_min',          label: 'Setup Minutes (per order)',     step: '0.1' },
  { field: 'programming_min',    label: 'Programming Minutes (per order)', step: '0.1' },
  { field: 'deburring_min',      label: 'Deburring Minutes / Part',      step: '0.01' },
  { field: 'runtime_min',        label: 'Runtime Minutes / Part',        step: '0.01' },
  { field: 'secondary_min',      label: 'Secondary Minutes / Part',      step: '0.01' },
  { field: 'inspection_min',     label: 'Inspection Minutes (per order)', step: '0.1' },
  { field: 'packaging_min',      label: 'Packaging Minutes (per order)', step: '0.1' },
];

// Excel-style key/value table. Numeric inputs always store the raw string the
// user types; the pricing engine coerces via Number(v || 0) downstream.
// `getDisplayValue` hides 0 as a dash placeholder ONLY when the field isn't
// focused — so the user can freely type "0" without it being stripped.
function InputTable({ title, rows, quote, readOnly, setQuote, setIsDirty }) {
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (row) => (e) => {
    const raw = e.target.value;
    setQuote((q) => ({ ...q, [row.field]: raw }));
    setIsDirty(true);
  };

  const getDisplayValue = (row) => {
    const v = quote?.[row.field];
    if (v == null || v === '') return '';
    // While the user is in this field, show the raw value so they can edit / type 0.
    if (focusedField === row.field) return v;
    // Otherwise hide explicit zeros so the dash placeholder takes over.
    if (Number(v) === 0) return '';
    return v;
  };

  return (
    <div className="border border-slate-300 rounded-sm overflow-hidden bg-white">
      <div className="px-3 py-2 border-b border-slate-300 bg-sky-50/70">
        <h4 className="text-sm font-bold" style={{ color: '#384f77' }}>{title}</h4>
      </div>
      {rows.map((row) => (
        <div
          key={row.field}
          className="grid grid-cols-[1.5fr_1fr] border-b border-slate-200 last:border-b-0"
        >
          <div className="px-3 py-2 text-sm text-slate-700 flex items-center">
            {row.label}
          </div>
          <div className="bg-amber-50/40 border-l border-slate-200 relative flex items-center cursor-text">
            <input
              type="number"
              step={row.step || '0.01'}
              value={getDisplayValue(row)}
              onChange={handleChange(row)}
              onFocus={(e) => { setFocusedField(row.field); e.target.select(); }}
              onBlur={() => setFocusedField(null)}
              disabled={readOnly}
              placeholder="—"
              className={`w-full bg-transparent text-sm text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-400 focus:bg-white disabled:text-slate-700 placeholder-slate-400 py-2 ${row.prefix ? 'pl-7' : 'pl-2'} ${row.suffix ? 'pr-7' : 'pr-2'}`}
            />
            {row.prefix && (
              <span className="absolute left-2 text-sm text-slate-500 pointer-events-none">
                {row.prefix}
              </span>
            )}
            {row.suffix && (
              <span className="absolute right-2 text-sm text-slate-500 pointer-events-none">
                {row.suffix}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Note: markup_pct is stored in app state as PERCENT (e.g. 15 for 15%).
// fetchQuote multiplies the DB decimal by 100; saveCore divides back.
// The pricing useMemo also normalizes to decimal before calling priceAllBreaks.
const COST_INPUTS = [
  { field: 'material_cost_per_part', label: 'Material Cost / Part',     step: '0.0001', prefix: '$' },
  { field: 'plating_total',          label: 'Plating Total / Order',    step: '0.01',   prefix: '$' },
  { field: 'tooling_total',          label: 'Tooling Total / Order',    step: '0.01',   prefix: '$' },
  { field: 'fixturing_total',        label: 'Fixturing Total / Order',  step: '0.01',   prefix: '$' },
  { field: 'shipping_total',         label: 'Shipping Total / Order',   step: '0.01',   prefix: '$' },
  { field: 'markup_pct',             label: 'Markup (15 = 15%)',        step: '0.1',    suffix: '%' },
  { field: 'minimum_lot_charge',     label: 'Minimum Lot Charge',       step: '0.01',   prefix: '$' },
  { field: 'round_to_nearest',       label: 'Round Price To Nearest',   step: '0.0001', prefix: '$' },
];

const EMPTY_QUOTE = {
  quote_number: '',
  status: 'draft',
  customer_id: null,
  contact: '',
  drawing_number: '',
  revision: '',
  description: '',
  material: '',
  quote_date: '',
  quote_valid_days: 30,
  quoted_by: '',
  customer_rfq_po: '',
  payment_terms: '',
  shipping_fob: '',
  lead_time: '',
  quantity_breaks: [0, 0, 0, 0, 0, 0],
  shop_rate_per_hr: '',
  setup_min: 0,
  programming_min: 0,
  deburring_min: 0,
  runtime_min: 0,
  secondary_min: 0,
  inspection_min: 0,
  packaging_min: 0,
  material_cost_per_part: 0,
  plating_total: 0,
  tooling_total: 0,
  fixturing_total: 0,
  shipping_total: 0,
  markup_pct: 0,
  minimum_lot_charge: 0,
  round_to_nearest: 0.01,
  customer_notes: '',
  internal_notes: '',
};

export default function CustomerQuote() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [draftChecked, setDraftChecked] = useState(false);

  const draftKey = id ? `customer-quote-draft:${id}` : null;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => { document.title = 'Customer Quote'; }, []);

  useEffect(() => {
    const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchQuote = async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customer_quotes')
        .select(`
          *,
          customers(id, name, nickname,
            billing_address, billing_city, billing_state, billing_zip,
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone)
        `)
        .eq('id', id)
        .single();
      if (error) throw error;
      // DB stores markup_pct as decimal (0.15). UI works in percent (15). Convert on load.
      setQuote({
        ...EMPTY_QUOTE,
        ...data,
        markup_pct: (Number(data.markup_pct) || 0) * 100,
      });
      setCustomer(data.customers || null);
      setIsDirty(false);
      setIsEditMode(data.status === 'draft');
    } catch (err) {
      console.error('Failed to load quote:', err);
      toast.error('Failed to load quote.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshQuote = () => fetchQuote(true);

  useEffect(() => {
    if (user) fetchQuote();
    // Depend on user?.id (stable) — `user` object reference changes on Supabase
    // auth token refresh, which would otherwise wipe in-progress edits on tab focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  // Restore unsaved local draft (if any) after initial fetch
  useEffect(() => {
    if (!quote || draftChecked || !draftKey) return;
    const draft = readLocalDraft(draftKey);
    if (draft?.data?.quote && draft.data.quote.id === id) {
      setQuote(draft.data.quote);
      setIsDirty(true);
      setIsEditMode(true);
      setRestoredFromDraft(true);
    }
    setDraftChecked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, draftKey, draftChecked]);

  // Auto-save edits to localStorage
  const draftSnapshot = useMemo(() => ({ quote }), [quote]);
  useLocalDraft({ key: draftKey, isDirty, snapshot: draftSnapshot });

  const dismissDraft = async () => {
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    await fetchQuote();
  };

  // ─── Pricing & warnings (live recompute) ───
  // UI keeps markup_pct in percent units; pricing engine wants decimal. Normalize here.
  const normalizedForPricing = useMemo(() => {
    if (!quote) return null;
    return { ...quote, markup_pct: (Number(quote.markup_pct) || 0) / 100 };
  }, [quote]);
  const results = useMemo(
    () => (normalizedForPricing ? priceAllBreaks(normalizedForPricing) : []),
    [normalizedForPricing]
  );
  const warnings = useMemo(
    () => (normalizedForPricing ? getQuoteWarnings(normalizedForPricing, results) : []),
    [normalizedForPricing, results]
  );
  const validForSend = useMemo(() => isQuoteValid(quote), [quote]);

  // ─── Mutation helpers ───
  const setField = (field) => (e) => {
    const val = e?.target?.value ?? e;
    setQuote((q) => ({ ...q, [field]: val }));
    setIsDirty(true);
  };

  const setNumField = (field) => (e) => {
    setQuote((q) => ({ ...q, [field]: e.target.value }));
    setIsDirty(true);
  };

  const handleCustomerChange = (customerId, row) => {
    setQuote((q) => ({ ...q, customer_id: customerId }));
    setCustomer(row);
    setIsDirty(true);
  };

  const handleBreakChange = (idx, value) => {
    setQuote((q) => {
      const next = [...(q.quantity_breaks || [0,0,0,0,0,0])];
      next[idx] = value === '' ? 0 : parseInt(value, 10) || 0;
      return { ...q, quantity_breaks: next };
    });
    setIsDirty(true);
  };

  // ─── Persistence ───
  const saveCore = async () => {
    if (!quote.quote_number?.trim()) {
      toast.error('Quote number is required.');
      return false;
    }
    if (quote.status === 'sent' && !validForSend) {
      toast.error('Cannot send — fill in customer, drawing #, shop rate, and at least one quantity break.');
      return false;
    }

    const num = (v) => (v === '' || v == null ? 0 : Number(v));

    const payload = {
      quote_number: quote.quote_number.trim(),
      status: quote.status || 'draft',
      customer_id: quote.customer_id || null,
      contact: quote.contact?.trim() || null,
      drawing_number: quote.drawing_number?.trim() || null,
      revision: quote.revision?.trim() || null,
      description: quote.description?.trim() || null,
      material: quote.material?.trim() || null,
      quote_date: quote.quote_date || new Date().toISOString().slice(0, 10),
      quote_valid_days: parseInt(quote.quote_valid_days, 10) || 30,
      quoted_by: quote.quoted_by?.trim() || null,
      customer_rfq_po: quote.customer_rfq_po?.trim() || null,
      payment_terms: quote.payment_terms || null,
      shipping_fob: quote.shipping_fob?.trim() || null,
      lead_time: quote.lead_time?.trim() || null,
      quantity_breaks: (quote.quantity_breaks || [0,0,0,0,0,0]).map((q) => parseInt(q, 10) || 0),
      shop_rate_per_hr: num(quote.shop_rate_per_hr),
      setup_min: num(quote.setup_min),
      programming_min: num(quote.programming_min),
      deburring_min: num(quote.deburring_min),
      runtime_min: num(quote.runtime_min),
      secondary_min: num(quote.secondary_min),
      inspection_min: num(quote.inspection_min),
      packaging_min: num(quote.packaging_min),
      material_cost_per_part: num(quote.material_cost_per_part),
      plating_total: num(quote.plating_total),
      tooling_total: num(quote.tooling_total),
      fixturing_total: num(quote.fixturing_total),
      shipping_total: num(quote.shipping_total),
      markup_pct: num(quote.markup_pct) / 100,
      minimum_lot_charge: num(quote.minimum_lot_charge),
      round_to_nearest: num(quote.round_to_nearest),
      customer_notes: quote.customer_notes?.trim() || null,
      internal_notes: quote.internal_notes?.trim() || null,
    };

    // Stamp lifecycle timestamps
    if (quote.status === 'sent' && !quote.sent_at) payload.sent_at = new Date().toISOString();
    if (quote.status === 'accepted' && !quote.accepted_at) payload.accepted_at = new Date().toISOString();
    if (quote.status === 'declined' && !quote.declined_at) payload.declined_at = new Date().toISOString();

    const { error } = await supabase.from('customer_quotes').update(payload).eq('id', id);
    if (error) throw error;
    return true;
  };

  const handleSave = async (exitOnSuccess = true) => {
    if (saving) return false;
    setSaving(true);
    try {
      const ok = await saveCore();
      if (!ok) return false;
      toast.success('Quote saved.');
      setIsDirty(false);
      clearLocalDraft(draftKey);
      setRestoredFromDraft(false);
      await refreshQuote();
      if (exitOnSuccess) setIsEditMode(false);
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.message || 'Failed to save quote.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const persistStatusChange = async (newStatus) => {
    try {
      const update = { status: newStatus };
      if (newStatus === 'sent' && !quote.sent_at) update.sent_at = new Date().toISOString();
      if (newStatus === 'accepted' && !quote.accepted_at) update.accepted_at = new Date().toISOString();
      if (newStatus === 'declined') update.declined_at = new Date().toISOString();
      const { error } = await supabase.from('customer_quotes').update(update).eq('id', id);
      if (error) throw error;
      toast.success('Status updated.');
      await refreshQuote();
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error(err.message || 'Failed to update status.');
    }
  };

  const handleQuickStatusChange = async (newStatus) => {
    if (!quote || newStatus === quote.status) return;
    if (newStatus === 'sent' && !validForSend) {
      toast.error('Cannot send — fill in customer, drawing #, shop rate, and at least one quantity break.');
      return;
    }
    await persistStatusChange(newStatus);
  };

  const handleDuplicate = async () => {
    try {
      const { generateQuoteNumber } = await import('../utils/quoteUtils');
      const newNumber = await generateQuoteNumber();
      const { data, error } = await supabase
        .from('customer_quotes')
        .insert({
          ...quote,
          id: undefined,
          quote_number: newNumber,
          status: 'draft',
          quote_date: new Date().toISOString().slice(0, 10),
          sent_at: null,
          accepted_at: null,
          declined_at: null,
          created_by: user?.id || null,
          created_at: undefined,
          updated_at: undefined,
          customers: undefined,
        })
        .select('id')
        .single();
      if (error) throw error;
      toast.success(`Duplicated as ${newNumber}.`);
      navigate(`/customer-quotes/${data.id}`);
    } catch (err) {
      console.error('Duplicate failed:', err);
      toast.error(err.message || 'Failed to duplicate.');
    }
  };

  const confirmDelete = async () => {
    try {
      const { error } = await supabase.from('customer_quotes').delete().eq('id', id);
      if (error) throw error;
      clearLocalDraft(draftKey);
      setDeleteOpen(false);
      setIsDirty(false);
      toast.success('Quote deleted.');
      navigate('/customer-quotes');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.message || 'Failed to delete quote.');
    }
  };

  const handleDownloadCustomerPDF = async () => {
    try {
      await generateCustomerQuotePDF({
        quote,
        customer,
        results,
      });
    } catch (err) {
      console.error('Customer PDF failed:', err);
      toast.error('Failed to generate customer PDF.');
    }
  };

  const handleDownloadInternalPDF = async () => {
    try {
      await generateInternalEstimatePDF({
        quote,
        customer,
        results,
      });
    } catch (err) {
      console.error('Internal PDF failed:', err);
      toast.error('Failed to generate internal PDF.');
    }
  };

  const enterEditMode = () => setIsEditMode(true);
  const handleCancelEdit = () => {
    if (isDirty) setDiscardConfirmOpen(true);
    else setIsEditMode(false);
  };
  const confirmDiscard = async () => {
    setDiscardConfirmOpen(false);
    setIsDirty(false);
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    await fetchQuote();
    setIsEditMode(false);
  };

  const blockerStay = () => blocker.reset?.();
  const blockerDiscard = () => {
    setIsDirty(false);
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    blocker.proceed?.();
  };
  const blockerSaveAndLeave = async () => { const ok = await handleSave(false); if (ok) blocker.proceed?.(); };

  if (loading) return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  if (!quote) {
    return (
      <div className="p-4">
        <Link to="/customer-quotes" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline">
          <ArrowLeftIcon className="w-4 h-4" /> Back to Quotes
        </Link>
        <p className="mt-4 text-sm text-slate-500">Quote not found.</p>
      </div>
    );
  }

  const readOnly = !isEditMode;
  const billTo = customer ? {
    name: customer.nickname || customer.name,
    contact: customer.billing_contact_name,
    address: customer.billing_address,
    city: customer.billing_city, state: customer.billing_state, zip: customer.billing_zip,
    email: customer.billing_contact_email,
    phone: customer.billing_contact_phone,
  } : null;
  const shipTo = customer ? {
    name: customer.nickname || customer.name,
    contact: customer.shipping_contact_name,
    address: customer.shipping_address,
    city: customer.shipping_city, state: customer.shipping_state, zip: customer.shipping_zip,
    email: customer.shipping_contact_email,
    phone: customer.shipping_contact_phone,
  } : null;

  return (
    <div className="p-4 space-y-4 max-w-7xl">
      <Link to="/customer-quotes" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Quotes
      </Link>

      {restoredFromDraft && (
        <div className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-between">
          <span>Restored unsaved changes from a previous session.</span>
          <button onClick={dismissDraft} className="text-rose-700 hover:underline ml-3">
            Discard local changes
          </button>
        </div>
      )}

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-lg font-semibold text-slate-800">{quote.quote_number}</h1>
          <PillSelect
            value={quote.status || 'draft'}
            onChange={(e) => readOnly ? handleQuickStatusChange(e.target.value) : setField('status')(e)}
            options={STATUS_OPTIONS}
            pillClass={STATUS_PILL[quote.status] || 'bg-slate-100 text-slate-700 border-slate-300'}
          />
          {isDirty && <span className="text-xs text-amber-700 italic">· unsaved changes</span>}
        </div>
        <div className="flex items-center gap-2">
          {readOnly ? (
            <button onClick={enterEditMode} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800">
              <PencilSquareIcon className="w-4 h-4" /> Edit
            </button>
          ) : (
            <>
              <button onClick={() => handleSave(true)} disabled={saving} className="px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={handleCancelEdit} disabled={saving} className="px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
            </>
          )}
          {readOnly && (
            <button onClick={handleDownloadCustomerPDF} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50">
              <ArrowDownTrayIcon className="w-4 h-4" /> Customer PDF
            </button>
          )}
          {readOnly && (
            <button onClick={handleDownloadInternalPDF} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50" title="Internal cost breakdown — never shown to customer">
              <ArrowDownTrayIcon className="w-4 h-4" /> Internal PDF
            </button>
          )}
          <button onClick={handleDuplicate} className="px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50">
            Duplicate
          </button>
          {quote.status === 'draft' && (
            <button onClick={() => setDeleteOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-500 border border-slate-200 rounded-sm hover:bg-rose-50 hover:text-rose-700" aria-label="Delete quote">
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-none" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Header form */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
        {/* Terms first */}
        <FormGrid columns={4}>
          <Select label="Payment Terms" value={quote.payment_terms || ''} onChange={setField('payment_terms')} options={PAYMENT_TERMS_OPTIONS} placeholder="— Select —" disabled={readOnly} />
          <Input label="Shipping / FOB" value={quote.shipping_fob || ''} onChange={setField('shipping_fob')} disabled={readOnly} />
          <Input label="Lead Time" value={quote.lead_time || ''} onChange={setField('lead_time')} disabled={readOnly} />
          <Input label="Customer RFQ/PO" value={quote.customer_rfq_po || ''} onChange={setField('customer_rfq_po')} disabled={readOnly} />
        </FormGrid>

        {/* Quote identity */}
        <FormGrid columns={4}>
          <Input label="Quote #" value={quote.quote_number || ''} onChange={setField('quote_number')} required disabled={readOnly} />
          <Input label="Quote Date" type="date" value={quote.quote_date || ''} onChange={setField('quote_date')} disabled={readOnly} />
          <Input label="Valid Days" type="number" min="0" value={quote.quote_valid_days ?? ''} onChange={setNumField('quote_valid_days')} disabled={readOnly} />
          <Input label="Quoted By" value={quote.quoted_by || ''} onChange={setField('quoted_by')} disabled={readOnly} />
        </FormGrid>

        {/* Customer */}
        <FormGrid columns={2}>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Customer</label>
            <CustomerPicker value={quote.customer_id} onChange={handleCustomerChange} isDisabled={readOnly} />
          </div>
          <Input label="Contact" value={quote.contact || ''} onChange={setField('contact')} disabled={readOnly} />
        </FormGrid>

        {/* Part info */}
        <FormGrid columns={3}>
          <Input label="Drawing #" value={quote.drawing_number || ''} onChange={setField('drawing_number')} required disabled={readOnly} />
          <Input label="Revision" value={quote.revision || ''} onChange={setField('revision')} disabled={readOnly} />
          <Input label="Material" value={quote.material || ''} onChange={setField('material')} disabled={readOnly} />
        </FormGrid>
        <Input label="Description" value={quote.description || ''} onChange={setField('description')} disabled={readOnly} />
      </div>

      {/* Customer + Ship-To preview */}
      <FormGrid columns={2}>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Bill To</h3>
          {billTo ? (
            <div className="text-sm text-slate-700 space-y-0.5">
              <div className="font-medium">{billTo.name}</div>
              {billTo.contact && <div>{billTo.contact}</div>}
              {billTo.address && <div>{billTo.address}</div>}
              {(billTo.city || billTo.state || billTo.zip) && (
                <div>{[billTo.city, billTo.state].filter(Boolean).join(', ')} {billTo.zip || ''}</div>
              )}
              {billTo.phone && <div className="text-slate-500">{billTo.phone}</div>}
              {billTo.email && <div className="text-slate-500">{billTo.email}</div>}
            </div>
          ) : <div className="text-sm text-slate-400">No customer selected.</div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Ship To</h3>
          {shipTo ? (
            <div className="text-sm text-slate-700 space-y-0.5">
              <div className="font-medium">{shipTo.name}</div>
              {shipTo.contact && <div>{shipTo.contact}</div>}
              {shipTo.address && <div>{shipTo.address}</div>}
              {(shipTo.city || shipTo.state || shipTo.zip) && (
                <div>{[shipTo.city, shipTo.state].filter(Boolean).join(', ')} {shipTo.zip || ''}</div>
              )}
              {shipTo.phone && <div className="text-slate-500">{shipTo.phone}</div>}
              {shipTo.email && <div className="text-slate-500">{shipTo.email}</div>}
            </div>
          ) : <div className="text-sm text-slate-400">No customer selected.</div>}
        </div>
      </FormGrid>

      {/* Pricing inputs — Excel-style two-column table */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputTable
          title="Labor Inputs"
          rows={LABOR_INPUTS}
          quote={quote}
          readOnly={readOnly}
          setQuote={setQuote}
          setIsDirty={setIsDirty}
        />
        <InputTable
          title="Cost Inputs"
          rows={COST_INPUTS}
          quote={quote}
          readOnly={readOnly}
          setQuote={setQuote}
          setIsDirty={setIsDirty}
        />
      </div>

      {/* Quantity breaks */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Quantity Breaks</h3>
        <p className="text-xs text-slate-500">Leave a slot at 0 (empty) to skip it on the customer quote.</p>
        <QuoteBreakInputs
          value={quote.quantity_breaks}
          onChange={handleBreakChange}
          disabled={readOnly}
        />
      </div>

      {/* Internal estimate */}
      <QuoteInternalEstimate results={results} />

      {/* Notes */}
      <FormGrid columns={2}>
        <Textarea label="Customer Notes (prints on quote)" value={quote.customer_notes || ''} onChange={setField('customer_notes')} rows={4} disabled={readOnly} />
        <Textarea label="Internal Notes (not printed)" value={quote.internal_notes || ''} onChange={setField('internal_notes')} rows={4} disabled={readOnly} />
      </FormGrid>

      {/* Discard / delete / navigation modals */}
      <Modal isOpen={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)} title="Discard changes?" size="md">
        <p className="text-sm text-slate-700">Your unsaved changes will be lost.</p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDiscardConfirmOpen(false)}>Keep editing</ModalButton>
          <ModalButton variant="danger" onClick={confirmDiscard}>Discard</ModalButton>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete quote?" size="md">
        <p className="text-sm text-slate-700">
          This will permanently delete <span className="font-medium">{quote.quote_number}</span>. This cannot be undone.
        </p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDeleteOpen(false)}>Cancel</ModalButton>
          <ModalButton variant="danger" onClick={confirmDelete}>Delete quote</ModalButton>
        </ModalFooter>
      </Modal>

      <Modal isOpen={blocker.state === 'blocked'} onClose={blockerStay} title="Unsaved changes" size="md">
        <p className="text-sm text-slate-700">You have unsaved changes on this quote. What would you like to do?</p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={blockerStay} disabled={saving}>Stay on page</ModalButton>
          <ModalButton variant="danger" onClick={blockerDiscard} disabled={saving}>Discard</ModalButton>
          <ModalButton variant="primary" onClick={blockerSaveAndLeave} loading={saving}>Save &amp; leave</ModalButton>
        </ModalFooter>
      </Modal>
    </div>
  );
}
